import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { TbCurrentLocation } from "react-icons/tb";
import axios from "axios";
import { useTranslation } from "react-i18next";
import styles from "../../../styles/__imagingOnboarding.module.scss";
import Header from "../../../components/header/Header";
import { toast } from "../../../components/toast";
import CountryCodeSelect from "../../../components/country-code-select/CountryCodeSelect";
import {
  DEFAULT_COUNTRY_ISO,
  findCountryByIso,
  splitMobileNumber,
} from "../../../constants/countries";
import CharCount from "../../../components/char-count/CharCount";
import { BIO_MAX_LENGTH } from "../../../utils/profileValidation";

// Onboarding is considered complete once the imaging center has a default address.
export const isImagingOnboarded = (addresses) =>
  Array.isArray(addresses) && addresses.some((a) => a?.is_default);

const WEEK_DAYS = [
  { code: "sun", label: "Sun" },
  { code: "mon", label: "Mon" },
  { code: "tue", label: "Tue" },
  { code: "wed", label: "Wed" },
  { code: "thu", label: "Thu" },
  { code: "fri", label: "Fri" },
  { code: "sat", label: "Sat" },
];
const ALL_DAY_CODES = WEEK_DAYS.map((d) => d.code);

const DEFAULT_START = "08:00";
const DEFAULT_END = "17:00";

// Build a full 7-day schedule from the profile. Prefers the per-day
// `operating_schedule`; falls back to the legacy `operating_days` +
// `operating_hours_start/end` so existing records keep working.
const buildScheduleFromProfile = (profile) => {
  const start = (profile?.operating_hours_start || DEFAULT_START).slice(0, 5);
  const end = (profile?.operating_hours_end || DEFAULT_END).slice(0, 5);
  const savedDays =
    Array.isArray(profile?.operating_days) && profile.operating_days.length > 0
      ? profile.operating_days
      : ALL_DAY_CODES;
  const savedSchedule = Array.isArray(profile?.operating_schedule)
    ? profile.operating_schedule
    : null;

  return WEEK_DAYS.map((d) => {
    if (savedSchedule) {
      const match = savedSchedule.find((s) => s.day === d.code);
      if (match) {
        return {
          day: d.code,
          is_open: !!match.is_open,
          start: (match.start || start).slice(0, 5),
          end: (match.end || end).slice(0, 5),
        };
      }
    }
    return { day: d.code, is_open: savedDays.includes(d.code), start, end };
  });
};

// Derive the legacy fields from the per-day schedule so older consumers
// (patient app, admin panel, etc.) keep working unchanged.
const deriveLegacyFromSchedule = (schedule) => {
  const openDays = schedule.filter((s) => s.is_open);
  const starts = openDays
    .map((s) => s.start)
    .filter(Boolean)
    .sort();
  const ends = openDays
    .map((s) => s.end)
    .filter(Boolean)
    .sort();
  return {
    operating_days: openDays.map((s) => s.day),
    operating_hours_start: starts[0] || DEFAULT_START,
    operating_hours_end: ends[ends.length - 1] || DEFAULT_END,
  };
};

const ImagingOnboarding = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    document.dir = lng === "ar" ? "rtl" : "ltr";
  };

  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // Step 1 — profile
  const [nameEnglish, setNameEnglish] = useState("");
  const [nameArabic, setNameArabic] = useState("");
  // Bilingual "About the imaging center" biography (mirrors the profile page).
  const [imagingBioEnglish, setImagingBioEnglish] = useState("");
  const [imagingBioArabic, setImagingBioArabic] = useState("");
  // Country (ISO) of the registered mobile number, detected from the saved number.
  const [countryIso, setCountryIso] = useState(DEFAULT_COUNTRY_ISO);
  // Same country list (with flags) as every other mobile number field in the apps.
  const selectedCountryData = findCountryByIso(countryIso);
  const [mobileNumber, setMobileNumber] = useState("");
  // Per-day operating schedule: [{ day, is_open, start, end }, ...] for all 7 days.
  const [operatingSchedule, setOperatingSchedule] = useState(() =>
    buildScheduleFromProfile(null),
  );

  const toggleDayOpen = (idx) =>
    setOperatingSchedule((prev) =>
      prev.map((r, i) => (i === idx ? { ...r, is_open: !r.is_open } : r)),
    );
  const updateDayTime = (idx, field, value) =>
    setOperatingSchedule((prev) =>
      prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)),
    );

  // Step 2 — address
  const [addressName, setAddressName] = useState("");
  const [addressFormData, setAddressFormData] = useState({
    house_flat_floor_no: "",
    street: "",
    street_arabic: "",
    area_district: "",
    area_district_arabic: "",
    city: "",
    city_arabic: "",
    governorate: "",
    governorate_arabic: "",
    country: "Iraq",
    region_id: "",
  });
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);

  // Map / autocomplete
  const [mapCenter, setMapCenter] = useState({ lat: 33.3152, lng: 44.3661 });
  const [markerPosition, setMarkerPosition] = useState(null);
  const [locationSearchQuery, setLocationSearchQuery] = useState("");
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  // Regions catalog (governorate-level) + the selected region's map bounds, used
  // to bias/restrict the location search to the chosen region.
  const [regions, setRegions] = useState([]);
  const [regionBounds, setRegionBounds] = useState(null);
  // True when the picked location is outside the selected region (shown inline
  // under the search box).
  const [regionMismatch, setRegionMismatch] = useState(false);
  // Region set on the user at admin-registration time; auto-selected in the
  // region dropdown once the regions catalog + Google Maps are ready.
  const [pendingRegionId, setPendingRegionId] = useState(null);
  const regionAutofilledRef = useRef(false);
  const mapRef = useRef(null);
  const sessionTokenRef = useRef(null);

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
  const loaderOptions = useMemo(
    () => ({
      googleMapsApiKey: apiKey,
      libraries: ["places", "marker"],
      id: "google-map-script",
    }),
    [apiKey],
  );
  const { isLoaded } = useJsApiLoader(loaderOptions);

  const axiosInstance = useCallback(() => {
    const token = sessionStorage.getItem("usr_1a2b3c");
    return axios.create({
      baseURL: process.env.REACT_APP_API_URL,
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
  }, []);

  // Prefill profile and short-circuit if already onboarded.
  useEffect(() => {
    (async () => {
      try {
        const api = axiosInstance();
        const [profileRes, addressRes] = await Promise.all([
          api.get("/api/v1/imaging/profile").catch(() => null),
          api.get("/api/v1/imaging/addresses").catch(() => null),
        ]);

        const addresses = addressRes?.data?.data || [];
        if (isImagingOnboarded(addresses)) {
          sessionStorage.setItem("usr_imaging_onboarded", "true");
          navigate("/imaging_dashboard", { replace: true });
          return;
        }

        const profile = profileRes?.data?.data;
        if (profile) {
          setNameEnglish(profile.imaging_center_name_english || "");
          setNameArabic(profile.imaging_center_name_arabic || "");
          setImagingBioEnglish(profile.bio_english || "");
          setImagingBioArabic(profile.bio_arabic || "");
          setOperatingSchedule(buildScheduleFromProfile(profile));
          // Region chosen when the imaging center user was created in the admin
          // panel — remember it so we can auto-select it in the address step.
          if (profile.region_id !== undefined && profile.region_id !== null) {
            setPendingRegionId(Number(profile.region_id));
          }
          if (profile.mobile_number) {
            const saved = splitMobileNumber(profile.mobile_number);
            setCountryIso(saved.country.iso);
            setMobileNumber(saved.national);
          }
        }
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load the governorate-level regions catalog for the region dropdown.
  useEffect(() => {
    (async () => {
      try {
        const res = await axiosInstance().get("/api/v1/auth/regions");
        const list = res?.data?.data || res?.data || [];
        setRegions(
          Array.isArray(list) ? list.filter((r) => r.is_active !== false) : [],
        );
      } catch (e) {
        // Non-fatal — the dropdown just stays empty if regions can't load.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When a region is picked: store region_id, prefill the governorate (EN+AR)
  // from the catalog, geocode the region to get its map bounds, and clear any
  // previously autofilled location values so stale data doesn't linger.
  const handleRegionChange = (value) => {
    const id = value ? Number(value) : "";
    const region = regions.find((r) => r.id === Number(value));
    setAddressFormData((prev) => ({
      ...prev,
      region_id: id,
      house_flat_floor_no: "",
      street: "",
      street_arabic: "",
      area_district: "",
      area_district_arabic: "",
      city: "",
      city_arabic: "",
      governorate: region?.region_name_english || "",
      governorate_arabic: region?.region_name_arabic || "",
    }));
    setMarkerPosition(null);
    setLatitude(null);
    setLongitude(null);
    setLocationSearchQuery("");
    setLocationSuggestions([]);
    setShowSuggestions(false);
    setRegionBounds(null);
    setRegionMismatch(false);
    if (region && window.google) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode(
        {
          address: `${region.region_name_english}, Iraq`,
          componentRestrictions: { country: "iq" },
        },
        (results, status) => {
          if (status === "OK" && results[0]) {
            const g = results[0].geometry;
            const bounds = g.bounds || g.viewport;
            if (bounds) setRegionBounds(bounds);
            if (g.location) {
              setMapCenter({ lat: g.location.lat(), lng: g.location.lng() });
            }
          }
        },
      );
    }
  };

  // Auto-select the region the imaging center was registered with (from the
  // admin panel) once the regions catalog and Google Maps are both ready. Runs
  // once; the officer can still change it manually afterwards.
  useEffect(() => {
    if (
      regionAutofilledRef.current ||
      !pendingRegionId ||
      !isLoaded ||
      regions.length === 0
    ) {
      return;
    }
    const exists = regions.some((r) => r.id === Number(pendingRegionId));
    if (!exists) return;
    regionAutofilledRef.current = true;
    handleRegionChange(String(pendingRegionId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingRegionId, isLoaded, regions]);

  // ---- Map helpers ----
  // English autofill. Merge (don't clobber) so the async Arabic autofill and the
  // selected region_id/governorate are preserved.
  const parseAddressComponents = useCallback((components) => {
    const patch = {};
    components.forEach((c) => {
      const types = c.types;
      if (types.includes("street_number") || types.includes("premise"))
        patch.house_flat_floor_no = c.long_name;
      if (types.includes("route")) patch.street = c.long_name;
      if (
        types.includes("sublocality") ||
        types.includes("sublocality_level_1") ||
        types.includes("neighborhood")
      )
        patch.area_district = c.long_name;
      if (types.includes("locality")) patch.city = c.long_name;
      if (types.includes("administrative_area_level_1"))
        patch.governorate = c.long_name;
      if (types.includes("country")) patch.country = c.long_name;
    });
    setAddressFormData((prev) => ({ ...prev, ...patch }));
  }, []);

  // Arabic autofill via the Geocoding REST API (language=ar), since the JS SDK
  // loads a single language. Best-effort: on failure the English fields remain.
  const fetchArabicAddressComponents = useCallback(
    async ({ placeId, lat, lng }) => {
      if (!apiKey) return;
      const param = placeId
        ? `place_id=${encodeURIComponent(placeId)}`
        : lat != null && lng != null
          ? `latlng=${lat},${lng}`
          : null;
      if (!param) return;
      try {
        const res = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?${param}&language=ar&key=${apiKey}`,
        );
        const json = await res.json();
        const components = json?.results?.[0]?.address_components;
        if (!components) return;
        const patch = {};
        for (const comp of components) {
          const types = comp.types;
          if (types.includes("route")) patch.street_arabic = comp.long_name;
          else if (
            types.includes("sublocality") ||
            types.includes("sublocality_level_1")
          )
            patch.area_district_arabic = comp.long_name;
          else if (types.includes("locality"))
            patch.city_arabic = comp.long_name;
          else if (types.includes("administrative_area_level_1"))
            patch.governorate_arabic = comp.long_name;
        }
        if (Object.keys(patch).length > 0) {
          setAddressFormData((prev) => ({ ...prev, ...patch }));
        }
      } catch (e) {
        // Non-fatal — Arabic autofill is best-effort.
      }
    },
    [apiKey],
  );

  // Returns true if the point is within the selected region's bounds. No region
  // selected (or bounds unresolved) → no restriction. Geometry, not names.
  const isWithinSelectedRegion = useCallback(
    (latLng) => {
      if (!addressFormData.region_id || !regionBounds || !window.google)
        return true;
      return regionBounds.contains(latLng);
    },
    [addressFormData.region_id, regionBounds],
  );

  const handleMapClick = useCallback(
    (event) => {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      if (!isWithinSelectedRegion(event.latLng)) {
        setRegionMismatch(true);
        toast.error(
          "This location is outside the selected region. Please pick an address within the selected region.",
        );
        return;
      }
      setRegionMismatch(false);
      setMarkerPosition({ lat, lng });
      setLatitude(lat);
      setLongitude(lng);
      if (window.google) {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
          if (status === "OK" && results[0]) {
            parseAddressComponents(results[0].address_components);
            fetchArabicAddressComponents({ lat, lng });
          }
        });
      }
    },
    [
      parseAddressComponents,
      fetchArabicAddressComponents,
      isWithinSelectedRegion,
    ],
  );

  // Marker drag: reject drops outside the selected region (snap back + warn).
  const handleMarkerDragEnd = useCallback(
    (event) => {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      if (!isWithinSelectedRegion(event.latLng)) {
        setRegionMismatch(true);
        toast.error(
          "This location is outside the selected region. Please pick an address within the selected region.",
        );
        setMarkerPosition((prev) => (prev ? { ...prev } : prev));
        return;
      }
      setRegionMismatch(false);
      setMarkerPosition({ lat, lng });
      setLatitude(lat);
      setLongitude(lng);
      if (window.google) {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
          if (status === "OK" && results[0]) {
            parseAddressComponents(results[0].address_components);
            fetchArabicAddressComponents({ lat, lng });
          }
        });
      }
    },
    [
      parseAddressComponents,
      fetchArabicAddressComponents,
      isWithinSelectedRegion,
    ],
  );

  const handleLocationSearch = useCallback(
    (query) => {
      setLocationSearchQuery(query);
      if (!query || query.length < 3 || !window.google) {
        setLocationSuggestions([]);
        setShowSuggestions(false);
        return;
      }
      if (!sessionTokenRef.current) {
        sessionTokenRef.current =
          new window.google.maps.places.AutocompleteSessionToken();
      }
      const service = new window.google.maps.places.AutocompleteService();
      service.getPlacePredictions(
        {
          input: query,
          sessionToken: sessionTokenRef.current,
          componentRestrictions: { country: "iq" },
          ...(regionBounds
            ? { locationRestriction: regionBounds, bounds: regionBounds }
            : {}),
        },
        (predictions, status) => {
          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            predictions
          ) {
            setLocationSuggestions(predictions);
            setShowSuggestions(true);
          } else {
            setLocationSuggestions([]);
            setShowSuggestions(false);
          }
        },
      );
    },
    [regionBounds],
  );

  const handleSelectSuggestion = useCallback(
    (suggestion) => {
      setLocationSearchQuery(suggestion.description);
      setShowSuggestions(false);
      setLocationSuggestions([]);
      sessionTokenRef.current = null;
      if (!window.google) return;
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ placeId: suggestion.place_id }, (results, status) => {
        if (status === "OK" && results[0]) {
          const loc = results[0].geometry.location;
          const latVal = loc.lat();
          const lngVal = loc.lng();
          setMarkerPosition({ lat: latVal, lng: lngVal });
          setMapCenter({ lat: latVal, lng: lngVal });
          setLatitude(latVal);
          setLongitude(lngVal);
          parseAddressComponents(results[0].address_components);
          fetchArabicAddressComponents({
            placeId: suggestion.place_id,
            lat: latVal,
            lng: lngVal,
          });
          if (addressFormData.region_id && regionBounds) {
            const inside = regionBounds.contains(loc);
            setRegionMismatch(!inside);
            if (!inside) {
              toast.error(
                "This location is outside the selected region. Please pick an address within the selected region.",
              );
            }
          } else {
            setRegionMismatch(false);
          }
        }
      });
    },
    [
      parseAddressComponents,
      fetchArabicAddressComponents,
      regionBounds,
      addressFormData.region_id,
    ],
  );

  const handleGetCurrentLocation = () => {
    if (!navigator.geolocation)
      return toast.error("Geolocation is not supported by your browser");
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        const latLng = window.google
          ? new window.google.maps.LatLng(lat, lng)
          : null;
        // If a region is selected and the current location falls outside it,
        // reject it (inline + toast) instead of autofilling out-of-region data.
        if (latLng && !isWithinSelectedRegion(latLng)) {
          setRegionMismatch(true);
          toast.error(
            "Your current location is outside the selected region. Please pick an address within the selected region.",
          );
          return;
        }
        setRegionMismatch(false);
        setMapCenter({ lat, lng });
        setMarkerPosition({ lat, lng });
        setLatitude(lat);
        setLongitude(lng);
        if (window.google) {
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === "OK" && results[0]) {
              parseAddressComponents(results[0].address_components);
              fetchArabicAddressComponents({ lat, lng });
            }
          });
        }
      },
      () => toast.error("Failed to get current location"),
    );
  };

  // ---- Step logic ----
  const validateStep1 = () => {
    const e = {};
    if (!nameEnglish.trim())
      e.nameEnglish = "Imaging center name (English) is required";
    if (!nameArabic.trim())
      e.nameArabic = "Imaging center name (Arabic) is required";
    // Same "About the Imaging Center" limit as the Imaging Profile page.
    const bioTooLong = `About the imaging center must be at most ${BIO_MAX_LENGTH} characters`;
    if (imagingBioEnglish.trim().length > BIO_MAX_LENGTH)
      e.imagingBioEnglish = bioTooLong;
    if (imagingBioArabic.trim().length > BIO_MAX_LENGTH)
      e.imagingBioArabic = bioTooLong;
    if (!mobileNumber.trim()) e.mobileNumber = "Mobile number is required";
    const openDays = operatingSchedule.filter((s) => s.is_open);
    if (!openDays.length) {
      e.operatingSchedule = "Select at least one operating day";
    } else if (openDays.some((s) => !s.start || !s.end)) {
      e.operatingSchedule = "Set the open and close time for each open day";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateStep2 = () => {
    const e = {};
    if (!addressFormData.region_id) e.region = "Region is required";
    if (!addressName.trim()) e.addressName = "Address name is required";
    if (!addressFormData.house_flat_floor_no.trim())
      e.house = "House/Flat/Floor is required";
    if (!addressFormData.street.trim())
      e.street = "Street (English) is required";
    if (!addressFormData.area_district.trim())
      e.area = "Area/District (English) is required";
    if (!addressFormData.city.trim()) e.city = "City (English) is required";
    if (!addressFormData.governorate.trim())
      e.governorate = "Governorate (English) is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = async () => {
    if (!validateStep1()) return;
    setSaving(true);
    try {
      const fullMobile = mobileNumber
        ? `${selectedCountryData.code}${mobileNumber}`
        : "";
      await axiosInstance().put("/api/v1/imaging/profile", {
        imaging_center_name_english: nameEnglish,
        imaging_center_name_arabic: nameArabic,
        operating_schedule: operatingSchedule,
        // Keep the legacy fields in sync for backward compatibility.
        ...deriveLegacyFromSchedule(operatingSchedule),
        mobile_number: fullMobile,
        // Send trimmed biographies; empty strings become null on the backend
        // so we don't persist blank "About" values.
        bio_english: imagingBioEnglish.trim(),
        bio_arabic: imagingBioArabic.trim(),
      });
      setErrors({});
      setCurrentStep(2);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async () => {
    if (!validateStep2()) return;
    setSaving(true);
    try {
      await axiosInstance().post("/api/v1/imaging/addresses", {
        name: addressName,
        ...addressFormData,
        latitude: latitude?.toString(),
        longitude: longitude?.toString(),
        is_default: true, // First address is always the default (onboarding requirement)
      });
      sessionStorage.setItem("usr_imaging_onboarded", "true");
      toast.success("Onboarding completed successfully");
      navigate("/imaging_dashboard", { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to save address");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <>
        <Header changeLanguage={changeLanguage} hideLanguage />
        <div className={styles["onboarding-page"]}>
          <div className={styles["onboarding-card"]}>Loading...</div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header changeLanguage={changeLanguage} hideLanguage />
      <div className={styles["onboarding-page"]}>
        <div className={styles["onboarding-card"]}>
          <h1 className={styles["title"]}>
            {t(
              "imaging.onboarding.title",
              "Complete Your Imaging Center Setup",
            )}
          </h1>
          <p className={styles["subtitle"]}>
            {t(
              "imaging.onboarding.subtitle",
              "Finish these steps to start using your Imaging Center account.",
            )}
          </p>

          {/* Stepper */}
          <div className={styles["stepper-container"]}>
            <div className={styles["stepper"]}>
              <div
                className={`${styles["step"]} ${currentStep >= 1 ? styles["active"] : ""} ${currentStep > 1 ? styles["completed"] : ""}`}
              >
                <div className={styles["step-number"]}>
                  {currentStep > 1 ? "✓" : "1"}
                </div>
                <div className={styles["step-label"]}>
                  {t("imaging.onboarding.step1", "Profile Information")}
                </div>
              </div>
              <div className={styles["step-line"]}></div>
              <div
                className={`${styles["step"]} ${currentStep >= 2 ? styles["active"] : ""}`}
              >
                <div className={styles["step-number"]}>2</div>
                <div className={styles["step-label"]}>
                  {t("imaging.onboarding.step2", "Address Information")}
                </div>
              </div>
            </div>
          </div>

          {currentStep === 1 && (
            <div className={styles["form-section"]}>
              <div className={styles["section-title"]}>
                {t("imaging.onboarding.step1", "Profile Information")}
              </div>
              <div className={styles["form-row"]}>
                <div className={styles["form-group"]}>
                  <label>
                    Imaging Center Name (English){" "}
                    <span style={{ color: "#dc3545" }}>*</span>
                  </label>
                  <input
                    className={styles["form-control"]}
                    value={nameEnglish}
                    onChange={(e) => setNameEnglish(e.target.value)}
                  />
                  {errors.nameEnglish && (
                    <span className={styles["error-text"]}>
                      {errors.nameEnglish}
                    </span>
                  )}
                </div>
                <div className={styles["form-group"]}>
                  <label>
                    Imaging Center Name (Arabic){" "}
                    <span style={{ color: "#dc3545" }}>*</span>
                  </label>
                  <input
                    className={styles["form-control"]}
                    dir="rtl"
                    value={nameArabic}
                    onChange={(e) => setNameArabic(e.target.value)}
                  />
                  {errors.nameArabic && (
                    <span className={styles["error-text"]}>
                      {errors.nameArabic}
                    </span>
                  )}
                </div>
              </div>

              <div className={styles["form-group"]}>
                <label>
                  Mobile Number <span style={{ color: "#dc3545" }}>*</span>
                </label>
                <div className={styles["phone-row"]}>
                  <CountryCodeSelect
                    className={styles["form-control"]}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      width: "120px",
                      flexShrink: 0,
                      background: "#f3f4f6",
                    }}
                    value={countryIso}
                    onChange={(country) => setCountryIso(country.iso)}
                    disabled
                  />
                  <input
                    className={styles["form-control"]}
                    value={mobileNumber}
                    readOnly
                    disabled
                    placeholder="Enter mobile number"
                    style={{
                      background: "#f3f4f6",
                      color: "#9ca3af",
                      cursor: "not-allowed",
                    }}
                  />
                </div>
                {errors.mobileNumber && (
                  <span className={styles["error-text"]}>
                    {errors.mobileNumber}
                  </span>
                )}
              </div>

              {/* Bilingual "About the imaging center" biography (mirrors the profile page). */}
              <div className={styles["form-group"]}>
                <label>About the Imaging Center (English)</label>
                <textarea
                  className={styles["form-control"]}
                  value={imagingBioEnglish}
                  onChange={(e) => setImagingBioEnglish(e.target.value)}
                  placeholder="Write a short introduction about the imaging center, modalities offered, and specialties."
                  rows={4}
                  maxLength={BIO_MAX_LENGTH}
                />
                <div className={styles["field-meta"]}>
                  {errors.imagingBioEnglish && (
                    <span className={styles["error-text"]}>
                      {errors.imagingBioEnglish}
                    </span>
                  )}
                  <CharCount
                    id="imagingBioEnglish-count"
                    value={imagingBioEnglish}
                    max={BIO_MAX_LENGTH}
                  />
                </div>
              </div>

              <div className={styles["form-group"]}>
                <label>About the Imaging Center (Arabic)</label>
                <textarea
                  className={styles["form-control"]}
                  dir="rtl"
                  value={imagingBioArabic}
                  onChange={(e) => setImagingBioArabic(e.target.value)}
                  placeholder="اكتب نبذة قصيرة عن مركز التصوير والفحوصات المقدمة والتخصصات."
                  rows={4}
                  maxLength={BIO_MAX_LENGTH}
                />
                <div className={styles["field-meta"]}>
                  {errors.imagingBioArabic && (
                    <span className={styles["error-text"]}>
                      {errors.imagingBioArabic}
                    </span>
                  )}
                  <CharCount
                    id="imagingBioArabic-count"
                    value={imagingBioArabic}
                    max={BIO_MAX_LENGTH}
                  />
                </div>
              </div>

              {/* Operating hours — each day has its own open/closed state + times */}
              <div className={styles["form-group"]}>
                <label>
                  Operating Hours <span style={{ color: "#dc3545" }}>*</span>
                </label>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  {operatingSchedule.map((row, idx) => {
                    const dayLabel =
                      WEEK_DAYS.find((d) => d.code === row.day)?.label ||
                      row.day;
                    return (
                      <div
                        key={row.day}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          flexWrap: "wrap",
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => toggleDayOpen(idx)}
                          style={{
                            minWidth: "72px",
                            padding: "8px 12px",
                            borderRadius: "999px",
                            border: `1px solid ${row.is_open ? "#099494" : "#d1d5db"}`,
                            background: row.is_open ? "#099494" : "#fff",
                            color: row.is_open ? "#fff" : "#374151",
                            fontSize: "13px",
                            fontWeight: 600,
                            cursor: "pointer",
                          }}
                        >
                          {dayLabel}
                        </button>

                        {row.is_open ? (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                            }}
                          >
                            <input
                              type="time"
                              className={styles["form-control"]}
                              value={row.start}
                              onChange={(e) =>
                                updateDayTime(idx, "start", e.target.value)
                              }
                            />
                            <span>—</span>
                            <input
                              type="time"
                              className={styles["form-control"]}
                              value={row.end}
                              onChange={(e) =>
                                updateDayTime(idx, "end", e.target.value)
                              }
                            />
                          </div>
                        ) : (
                          <span style={{ color: "#9ca3af", fontSize: "13px" }}>
                            Closed
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
                {errors.operatingSchedule && (
                  <span className={styles["error-text"]}>
                    {errors.operatingSchedule}
                  </span>
                )}
              </div>

              <div className={styles["footer"]}>
                <span />
                <button
                  className={`${styles["btn"]} ${styles["btn-primary"]}`}
                  onClick={handleNext}
                  disabled={saving}
                >
                  {saving ? "Saving..." : t("imaging.onboarding.next", "Next")}
                </button>
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className={styles["form-section"]}>
              <div className={styles["section-title"]}>
                {t("imaging.onboarding.step2", "Address Information")}
              </div>

              <div className={styles["map-wrap"]}>
                {isLoaded ? (
                  <>
                    <GoogleMap
                      mapContainerStyle={{
                        width: "100%",
                        height: "280px",
                        borderRadius: "8px",
                      }}
                      center={mapCenter}
                      zoom={13}
                      onClick={handleMapClick}
                      onLoad={(map) => (mapRef.current = map)}
                    >
                      {markerPosition && (
                        <Marker
                          position={markerPosition}
                          draggable
                          onDragEnd={handleMarkerDragEnd}
                        />
                      )}
                    </GoogleMap>
                    <button
                      type="button"
                      className={styles["current-location-btn"]}
                      onClick={handleGetCurrentLocation}
                    >
                      <TbCurrentLocation />{" "}
                      {t(
                        "imaging.profile.useCurrentLocation",
                        "Use Current Location",
                      )}
                    </button>
                  </>
                ) : (
                  <p>Loading map...</p>
                )}
              </div>

              {/* Region — restricts the location search to the selected region */}
              <div className={styles["form-group"]}>
                <label>
                  Region <span style={{ color: "#dc3545" }}>*</span>
                </label>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#0f5b5b",
                    background: "#e7fafa",
                    border: "1px solid #cdefef",
                    borderRadius: "6px",
                    padding: "6px 10px",
                    margin: "2px 0 8px",
                  }}
                >
                  Selecting a region restricts the map search to that area and
                  autofills the address fields below. Locations outside the
                  selected region aren't allowed.
                </div>
                <select
                  className={styles["form-control"]}
                  value={
                    addressFormData.region_id === ""
                      ? ""
                      : String(addressFormData.region_id)
                  }
                  onChange={(e) => handleRegionChange(e.target.value)}
                >
                  <option value="">Select a region</option>
                  {regions.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.region_name_english}
                    </option>
                  ))}
                </select>
                {errors.region && (
                  <span className={styles["error-text"]}>{errors.region}</span>
                )}
              </div>

              <div
                className={styles["form-group"]}
                style={{ position: "relative" }}
              >
                <label>Search Location</label>
                <input
                  className={styles["form-control"]}
                  value={locationSearchQuery}
                  onChange={(e) => handleLocationSearch(e.target.value)}
                  placeholder="Search for a location to autofill address..."
                  autoComplete="off"
                  disabled={!isLoaded}
                />
                {showSuggestions && locationSuggestions.length > 0 && (
                  <ul className={styles["location-suggestions"]}>
                    {locationSuggestions.map((s) => (
                      <li
                        key={s.place_id}
                        className={styles["suggestion-item"]}
                        onClick={() => handleSelectSuggestion(s)}
                      >
                        {s.description}
                      </li>
                    ))}
                  </ul>
                )}
                {regionMismatch && (
                  <span
                    style={{
                      display: "block",
                      marginTop: "6px",
                      color: "#dc2626",
                      fontSize: "13px",
                    }}
                  >
                    This location is outside the selected region. Please pick an
                    address within the selected region.
                  </span>
                )}
              </div>

              <div className={styles["form-group"]}>
                <label>
                  Address Name <span style={{ color: "#dc3545" }}>*</span>
                </label>
                <input
                  className={styles["form-control"]}
                  value={addressName}
                  onChange={(e) => setAddressName(e.target.value)}
                  placeholder="e.g., Main Branch"
                />
                {errors.addressName && (
                  <span className={styles["error-text"]}>
                    {errors.addressName}
                  </span>
                )}
              </div>

              <div className={styles["form-row"]}>
                <div className={styles["form-group"]}>
                  <label>
                    House/Flat/Floor No{" "}
                    <span style={{ color: "#dc3545" }}>*</span>
                  </label>
                  <input
                    className={styles["form-control"]}
                    value={addressFormData.house_flat_floor_no}
                    onChange={(e) =>
                      setAddressFormData({
                        ...addressFormData,
                        house_flat_floor_no: e.target.value,
                      })
                    }
                  />
                  {errors.house && (
                    <span className={styles["error-text"]}>{errors.house}</span>
                  )}
                </div>
                <div className={styles["form-group"]}>
                  <label>
                    Street (English) <span style={{ color: "#dc3545" }}>*</span>
                  </label>
                  <input
                    className={styles["form-control"]}
                    value={addressFormData.street}
                    onChange={(e) =>
                      setAddressFormData({
                        ...addressFormData,
                        street: e.target.value,
                      })
                    }
                  />
                  {errors.street && (
                    <span className={styles["error-text"]}>
                      {errors.street}
                    </span>
                  )}
                </div>
              </div>

              <div className={styles["form-row"]}>
                <div className={styles["form-group"]}>
                  <label>Street (Arabic)</label>
                  <input
                    className={styles["form-control"]}
                    dir="rtl"
                    value={addressFormData.street_arabic}
                    onChange={(e) =>
                      setAddressFormData({
                        ...addressFormData,
                        street_arabic: e.target.value,
                      })
                    }
                  />
                </div>
                <div className={styles["form-group"]}>
                  <label>
                    Area/District (English){" "}
                    <span style={{ color: "#dc3545" }}>*</span>
                  </label>
                  <input
                    className={styles["form-control"]}
                    value={addressFormData.area_district}
                    onChange={(e) =>
                      setAddressFormData({
                        ...addressFormData,
                        area_district: e.target.value,
                      })
                    }
                  />
                  {errors.area && (
                    <span className={styles["error-text"]}>{errors.area}</span>
                  )}
                </div>
              </div>

              <div className={styles["form-row"]}>
                <div className={styles["form-group"]}>
                  <label>Area/District (Arabic)</label>
                  <input
                    className={styles["form-control"]}
                    dir="rtl"
                    value={addressFormData.area_district_arabic}
                    onChange={(e) =>
                      setAddressFormData({
                        ...addressFormData,
                        area_district_arabic: e.target.value,
                      })
                    }
                  />
                </div>
                <div className={styles["form-group"]}>
                  <label>
                    City (English) <span style={{ color: "#dc3545" }}>*</span>
                  </label>
                  <input
                    className={styles["form-control"]}
                    value={addressFormData.city}
                    onChange={(e) =>
                      setAddressFormData({
                        ...addressFormData,
                        city: e.target.value,
                      })
                    }
                  />
                  {errors.city && (
                    <span className={styles["error-text"]}>{errors.city}</span>
                  )}
                </div>
              </div>

              <div className={styles["form-row"]}>
                <div className={styles["form-group"]}>
                  <label>City (Arabic)</label>
                  <input
                    className={styles["form-control"]}
                    dir="rtl"
                    value={addressFormData.city_arabic}
                    onChange={(e) =>
                      setAddressFormData({
                        ...addressFormData,
                        city_arabic: e.target.value,
                      })
                    }
                  />
                </div>
                <div className={styles["form-group"]}>
                  <label>
                    Governorate (English){" "}
                    <span style={{ color: "#dc3545" }}>*</span>
                  </label>
                  <input
                    className={styles["form-control"]}
                    value={addressFormData.governorate}
                    onChange={(e) =>
                      setAddressFormData({
                        ...addressFormData,
                        governorate: e.target.value,
                      })
                    }
                  />
                  {errors.governorate && (
                    <span className={styles["error-text"]}>
                      {errors.governorate}
                    </span>
                  )}
                </div>
              </div>

              <div className={styles["form-group"]}>
                <label>Governorate (Arabic)</label>
                <input
                  className={styles["form-control"]}
                  dir="rtl"
                  value={addressFormData.governorate_arabic}
                  onChange={(e) =>
                    setAddressFormData({
                      ...addressFormData,
                      governorate_arabic: e.target.value,
                    })
                  }
                />
              </div>

              <div className={styles["checkbox-row"]}>
                <input type="checkbox" checked readOnly />
                <span>
                  {t(
                    "imaging.onboarding.defaultNote",
                    "This address will be set as your default address.",
                  )}
                </span>
              </div>

              <div className={styles["footer"]}>
                <button
                  className={`${styles["btn"]} ${styles["btn-outline"]}`}
                  onClick={() => {
                    setErrors({});
                    setCurrentStep(1);
                  }}
                  disabled={saving}
                >
                  {t("imaging.onboarding.back", "Back")}
                </button>
                <button
                  className={`${styles["btn"]} ${styles["btn-primary"]}`}
                  onClick={handleComplete}
                  disabled={saving}
                >
                  {saving
                    ? "Saving..."
                    : t("imaging.onboarding.complete", "Complete Setup")}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ImagingOnboarding;
